/**
 * UserDetailDrawer — Drawer de detalhe de usuário (Aba 2 Brief 5 A1).
 *
 * Pattern D-B: cada seção sensível (audit history, deactivate) tem gate próprio.
 * Pattern ADR-071: PATCH usa If-Match via useUpdateUser (version passado).
 *
 * Seções:
 *   - Perfil: nome + email + role + status + MFA.
 *   - Edição inline se capability users-update.
 *   - Desativação se capability users-deactivate.
 *   - Histórico de auditoria GET /v1/audit-events?entityType=user&entityId=:id
 *     (gate: audit-events-read).
 */

import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import {
  useDeactivateUser,
  useGeneratePasswordResetLink,
  useUpdateUser,
  useUser,
} from '../../../hooks/use-users';

import { PasswordResetLinkPanel } from './PasswordResetLinkPanel';
import { USER_ROLE_LABELS } from './types';
import { UserAuditHistory } from './UserAuditHistory';
import { UserAvatar } from './UserAvatar';
import { UserStatusBadge } from './UserStatusBadge';

import type { PasswordResetLinkWire, UpdateUserBody } from './types';

const EDITABLE_ROLES = [
  { value: 'atendente', label: 'Atendente' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'gestor', label: 'Gestor' },
  { value: 'admin_tenant', label: 'Admin' },
  { value: 'dpo_tenant', label: 'DPO' },
];

interface Props {
  userId: string;
  onClose: () => void;
}

export function UserDetailDrawer({ userId, onClose }: Props): JSX.Element {
  const canUpdate = useCapability('users-update');
  const canDeactivate = useCapability('users-deactivate');

  const { data: user, isLoading, isError } = useUser(userId);
  const updateMut = useUpdateUser();
  const deactivateMut = useDeactivateUser();
  const resetLinkMut = useGeneratePasswordResetLink();
  const [resetLink, setResetLink] = useState<PasswordResetLinkWire | null>(null);
  const [resetLinkError, setResetLinkError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [deactivateReason, setDeactivateReason] = useState('');
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  function startEdit(): void {
    if (!user) return;
    setEditName(user.name);
    setEditRole(user.primaryRole);
    setEditMode(true);
  }

  async function handleSave(): Promise<void> {
    if (!user) return;
    const body: UpdateUserBody = {};
    if (editName.trim() && editName.trim() !== user.name) body.name = editName.trim();
    if (editRole && editRole !== user.primaryRole) body.primaryRole = editRole;
    if (Object.keys(body).length === 0) {
      setEditMode(false);
      return;
    }
    await updateMut.mutateAsync({ id: user.id, body, version: user.version });
    setEditMode(false);
  }

  async function handleDeactivate(): Promise<void> {
    if (!user || deactivateReason.trim().length < 5) return;
    await deactivateMut.mutateAsync({ id: user.id, body: { reason: deactivateReason.trim() } });
    setShowDeactivateModal(false);
    setDeactivateReason('');
    onClose();
  }

  async function handleResetLink(): Promise<void> {
    if (!user) return;
    setResetLinkError(null);
    try {
      setResetLink(await resetLinkMut.mutateAsync(user.id));
    } catch {
      setResetLinkError('Não foi possível gerar o link. Tente de novo.');
    }
  }

  function formatDate(val: string | null): string {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden="true" />

      {/* Drawer panel */}
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-[480px] flex-col bg-surface shadow-2xl"
        role="dialog"
        aria-label="Detalhe do usuário"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
          <h2 className="text-base font-semibold text-text-primary">Detalhe do usuário</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-text-secondary hover:bg-surface-raised hover:text-text-primary"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <span className="text-sm text-text-secondary">Carregando...</span>
            </div>
          )}

          {isError && (
            <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              Erro ao carregar usuário.
            </div>
          )}

          {user && (
            <div className="flex flex-col gap-6">
              {/* Avatar + identidade */}
              <div className="flex items-center gap-4">
                <UserAvatar id={user.id} name={user.name} />
                <div>
                  <p className="font-semibold text-text-primary">{user.name}</p>
                  <p className="text-sm text-text-secondary">{user.email}</p>
                  <UserStatusBadge status={user.status} />
                </div>
              </div>

              {/* Formulário de edição */}
              {editMode && canUpdate ? (
                <div className="rounded-lg border border-border-subtle p-4 flex flex-col gap-3">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      Nome
                    </label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      Papel
                    </label>
                    <select
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                      className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm"
                    >
                      {EDITABLE_ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setEditMode(false)}
                      className="rounded border border-border-subtle px-4 py-2 text-sm"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleSave()}
                      disabled={updateMut.isPending}
                      className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                    >
                      {updateMut.isPending ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                  {updateMut.isError && (
                    <p className="text-xs text-red-600">
                      Erro ao salvar. Verifique se a versão foi atualizada (conflito ADR-071).
                    </p>
                  )}
                </div>
              ) : (
                /* Modo leitura */
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <dt className="font-medium text-text-secondary">Papel</dt>
                    <dd className="text-text-primary">
                      {USER_ROLE_LABELS[user.primaryRole] ?? user.primaryRole}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-text-secondary">Status</dt>
                    <dd>
                      <UserStatusBadge status={user.status} />
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-text-secondary">MFA</dt>
                    <dd className="text-text-primary">
                      {user.mfaEnabled
                        ? 'Ativado'
                        : user.mfaPending
                          ? 'Pendente setup'
                          : 'Desativado'}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-text-secondary">Versão</dt>
                    <dd className="font-mono text-text-primary">{user.version}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-text-secondary">Criado em</dt>
                    <dd className="text-text-primary">{formatDate(user.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-text-secondary">Último login</dt>
                    <dd className="text-text-primary">{formatDate(user.lastLoginAt)}</dd>
                  </div>
                  {user.archivedAt && (
                    <div className="col-span-2">
                      <dt className="font-medium text-text-secondary">Desativado em</dt>
                      <dd className="text-text-primary">{formatDate(user.archivedAt)}</dd>
                      {user.archivedReason && (
                        <dd className="mt-0.5 text-xs text-text-secondary italic">
                          {user.archivedReason}
                        </dd>
                      )}
                    </div>
                  )}
                </dl>
              )}

              {/* Item 477 — link de redefinição de senha gerado pelo gestor (gate users-update) */}
              {canUpdate && user.status === 'active' && (
                <section className="mt-6 space-y-2" aria-label="Senha">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                    Senha
                  </h4>
                  {resetLink === null ? (
                    <>
                      <p className="text-xs text-text-secondary">
                        Esqueceu a senha? Gere um link de redefinição e entregue à pessoa. Vale 1
                        hora e só pode ser usado uma vez.
                      </p>
                      <button
                        type="button"
                        className="rounded border border-border-subtle px-3 py-1.5 text-sm hover:bg-surface-raised"
                        disabled={resetLinkMut.isPending}
                        onClick={() => void handleResetLink()}
                      >
                        {resetLinkMut.isPending ? 'Gerando…' : 'Gerar link de redefinição de senha'}
                      </button>
                      {resetLinkError !== null && (
                        <p className="text-xs text-red-600" role="alert">
                          {resetLinkError}
                        </p>
                      )}
                    </>
                  ) : (
                    <PasswordResetLinkPanel link={resetLink} onClose={() => setResetLink(null)} />
                  )}
                </section>
              )}

              {/* D-B: Histórico de auditoria — gate próprio audit-events-read */}
              <UserAuditHistory userId={user.id} />
            </div>
          )}
        </div>

        {/* Footer com ações */}
        {user && !editMode && (
          <div className="flex items-center justify-between border-t border-border-subtle px-6 py-4">
            {/* D-B: Editar — gate users-update */}
            {canUpdate && user.status === 'active' && (
              <button
                type="button"
                onClick={startEdit}
                className="rounded border border-border-subtle px-4 py-2 text-sm hover:bg-surface-raised"
              >
                Editar
              </button>
            )}
            {/* D-B: Desativar — gate users-deactivate */}
            {canDeactivate && user.status === 'active' && (
              <button
                type="button"
                onClick={() => setShowDeactivateModal(true)}
                className="ml-auto rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Desativar
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal desativar */}
      {showDeactivateModal && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-surface p-6 shadow-xl">
            <h3 className="text-base font-semibold text-text-primary">Desativar usuário</h3>
            <p className="mt-2 text-sm text-text-secondary">
              O histórico é preservado para auditoria. O usuário perde acesso imediato.
            </p>
            <label className="mt-4 block text-sm font-medium text-text-primary">
              Motivo (mínimo 5 caracteres)
            </label>
            <textarea
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm"
              placeholder="Ex: Desligamento da empresa"
            />
            <div className="mt-4 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowDeactivateModal(false);
                  setDeactivateReason('');
                }}
                className="rounded border border-border-subtle px-4 py-2 text-sm"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleDeactivate()}
                disabled={deactivateReason.trim().length < 5 || deactivateMut.isPending}
                className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
              >
                {deactivateMut.isPending ? 'Processando...' : 'Confirmar desativação'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
